export type SignalId = 'SIGINT' | 'SIGTSTP';

export const signalsDemoGlossary = [
  {
    key: 'Ctrl+C',
    signal: 'SIGINT' as SignalId,
    name: 'Interrupt',
    action: 'Stop the running program',
    description:
      "Sends SIGINT (signal interrupt) to the foreground process. Most programs respond by stopping immediately. It's how you cancel a long-running command or exit a program that's stuck.",
    example:
      'Running `sleep 100` and pressing Ctrl+C stops it immediately.',
  },
  {
    key: 'Ctrl+Z',
    signal: 'SIGTSTP' as SignalId,
    name: 'Suspend',
    action: 'Pause and move to background',
    description:
      'Sends SIGTSTP (signal terminal stop) to suspend the process. The program pauses but stays in memory. Use `fg` to resume it in the foreground, or `bg` to continue it in the background.',
    example:
      'Suspend vim with Ctrl+Z, run another command, then type `fg` to return.',
  },
];

import { SITE_VOICE } from '../siteVoice';

export type SignalExplainerStep = 'what' | 'how' | 'path' | 'handling';

export const signalsExplainerSteps = {
  what: {
    title: 'What Are Signals?',
    description:
      'Signals are a way for the operating system to communicate with running programs. When you press Ctrl+C, your terminal sends a byte (0x03) to the PTY—but the kernel intercepts it and converts it into a signal before your program ever sees it.',
  },
  how: {
    title: 'The Line Discipline',
    description:
      "The kernel's PTY has a component called the line discipline. It inspects every byte passing through. When it sees 0x03 (Ctrl+C) and signal handling is enabled, it generates SIGINT instead of passing the byte to the program. This happens in the kernel, not in your terminal emulator.",
  },
  path: {
    title: 'The Signal Path',
    description:
      'When you press Ctrl+C: 1) Your terminal sends byte 0x03 to the PTY master. 2) The kernel line discipline intercepts it. 3) Instead of forwarding the byte, it sends SIGINT to the foreground process group. 4) The process handles or terminates.',
  },
  handling: {
    title: 'Signal Handling',
    description:
      'Programs can choose how to handle signals. Some ignore Ctrl+C (like vim during certain operations). Some catch it to clean up before exiting. Some (like sleep) just die immediately. The default is to terminate.',
  },
} satisfies Record<
  SignalExplainerStep,
  { title: string; description: string }
>;

export const signalsDemoInteractive = {
  tryIt: 'Try It',
  runCommand: 'Run Command',
  reset: 'Reset',
  statePrefix: 'State: ',
  interrupted: 'Process interrupted (killed)',
  suspended: 'Process suspended (use `fg` to resume)',
  hint:
    'Click "Run Command" to start a simulated process, then try sending different signals.',
  terminalLines: {
    idle: `${SITE_VOICE.promptPath} $ `,
    sleeping: `${SITE_VOICE.promptPath} $ sleep 100`,
    sleepingMsg: 'sleeping...',
  },
  simulatedOutput: {
    interrupted: '^C',
    suspendedCtrlZ: '^Z',
    suspendedLine: '[1]+  Stopped                 sleep 100',
  },
} as const;

export const signalsVsKeys = {
  heading: 'Signals vs Regular Keys',
  regularHeading: 'Regular Keys',
  signalHeading: 'Signal Keys',
  regularRows: [
    { kind: 'hex' as const, key: 'a', value: '0x61', note: '(character sent to program)' },
    { kind: 'hex' as const, key: 'Enter', value: '0x0D', note: '(carriage return)' },
    { kind: 'seq' as const, key: '↑', value: '^[[A', note: '(escape sequence)' },
  ],
  signalRowsFooter:
    'Bytes intercepted by line discipline → converted to signals',
  footer:
    'Regular keys become bytes that flow through the PTY to the program. Signal keys also become bytes, but the kernel\'s line discipline intercepts them and generates OS signals before they reach the program.',
} as const;
